import React, { useState, useEffect } from "react";
import { fetchChatbotConfigFull, updateChatbotConfig } from "../../services/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { useToast } from "../../hooks/use-toast";
import { Loader2, Plus, X, Save, MessageSquare } from "lucide-react";

const AdminChatbot = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchChatbotConfigFull().then(setCfg).catch(() => {});
  }, []);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const setReply = (i, v) => set("quickReplies", cfg.quickReplies.map((r, idx) => (idx === i ? v : r)));
  const addReply = () => set("quickReplies", [...(cfg.quickReplies || []), ""]);
  const removeReply = (i) => set("quickReplies", cfg.quickReplies.filter((_, idx) => idx !== i));

  const setFaq = (i, k, v) => set("faqs", cfg.faqs.map((f, idx) => (idx === i ? { ...f, [k]: v } : f)));
  const addFaq = () => set("faqs", [...(cfg.faqs || []), { keywords: [], answer: "" }]);
  const removeFaq = (i) => set("faqs", cfg.faqs.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        welcome: cfg.welcome,
        fallback: cfg.fallback,
        quickReplies: (cfg.quickReplies || []).filter(Boolean),
        faqs: (cfg.faqs || [])
          .map((f) => ({
            keywords: Array.isArray(f.keywords)
              ? f.keywords
              : String(f.keywords).split(",").map((s) => s.trim()).filter(Boolean),
            answer: f.answer,
          }))
          .filter((f) => f.answer && f.keywords.length),
      };
      await updateChatbotConfig(payload);
      toast({ title: "Chatbot settings saved" });
    } catch (e) {
      toast({ title: "Save failed", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 text-[#C9A227] animate-spin" /></div>;

  return (
    <div className="max-w-3xl">
      <div className="flex items-center gap-2 mb-5">
        <MessageSquare className="w-5 h-5 text-[#C9A227]" />
        <h2 className="font-display font-bold text-lg text-[#14171c]">Chatbot Settings</h2>
      </div>

      <div className="bg-white rounded-xl border border-neutral-200 p-6 space-y-5">
        <div>
          <Label>Welcome Message</Label>
          <Textarea className="mt-1.5" rows={2} value={cfg.welcome || ""} onChange={(e) => set("welcome", e.target.value)} />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>Quick Reply Buttons</Label>
            <button type="button" onClick={addReply} className="inline-flex items-center gap-1 text-sm font-semibold text-[#C9A227]"><Plus className="w-4 h-4" /> Add</button>
          </div>
          <div className="space-y-2">
            {(cfg.quickReplies || []).map((r, i) => (
              <div key={i} className="flex gap-2">
                <Input value={r} onChange={(e) => setReply(i, e.target.value)} placeholder="Quick reply text" />
                <button type="button" onClick={() => removeReply(i)} className="text-red-400 hover:text-red-600 px-1"><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <Label>Fallback Message (when no FAQ matches)</Label>
          <Textarea className="mt-1.5" rows={2} value={cfg.fallback || ""} onChange={(e) => set("fallback", e.target.value)} />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>FAQ Answers</Label>
            <button type="button" onClick={addFaq} className="inline-flex items-center gap-1 text-sm font-semibold text-[#C9A227]"><Plus className="w-4 h-4" /> Add FAQ</button>
          </div>
          <p className="text-xs text-neutral-500 mb-3">The bot replies with an answer when the visitor's message contains any of its keywords.</p>
          <div className="space-y-3">
            {(cfg.faqs || []).map((f, i) => (
              <div key={i} className="border border-neutral-200 rounded-lg p-3 relative">
                <button type="button" onClick={() => removeFaq(i)} className="absolute top-2 right-2 text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                <Label className="text-xs">Keywords (comma separated)</Label>
                <Input className="mt-1 mb-2"
                  value={Array.isArray(f.keywords) ? f.keywords.join(", ") : f.keywords}
                  onChange={(e) => setFaq(i, "keywords", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                  placeholder="e.g. moq, minimum, minimum order" />
                <Label className="text-xs">Answer</Label>
                <Textarea className="mt-1" rows={2} value={f.answer} onChange={(e) => setFaq(i, "answer", e.target.value)} />
              </div>
            ))}
          </div>
        </div>

        <Button onClick={save} disabled={saving} className="bg-[#C9A227] hover:bg-[#A6841B] text-white">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save Chatbot Settings
        </Button>
      </div>
    </div>
  );
};

export default AdminChatbot;
